import React, { useCallback, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import { Button } from "devextreme-react";
import { DataGrid, Column, LoadPanel as GridLoadPanel, Scrolling } from "devextreme-react/data-grid";
import { createStore } from "devextreme-aspnet-data-nojquery";
import { alert } from "devextreme/ui/dialog";
import routes from "../../../../../../assets/apiRoutes.json";
import { MESSAGES } from "../../../../../../common/Messages";
import { EMPTY_GUID, RecordTypes, TransactionStage } from "../../../../../../enum/Enum";
import ChargeAndHoldShipmentPopup from "../../common/ChargeAndHoldShipmentPopup";

const quantityFormat = { type: "fixedPoint", precision: 2 };
// uOMD0000 EditChargeAndHoldShipmentLocations: the header stage must be Assigned, Picked/Staged or Completed.
const stagesAllowingShipItemChanges = [TransactionStage.Assigned, TransactionStage.Picked, TransactionStage.Completed];

/*
 * Delphi uOMD0000 tabCHShipmentLoc ("C&H Ship Inv"): the selected detail line's Charge & Hold ship
 * rows (uOMM0000 qryCHShipmentTransLocation), read-only, and the "Change" button that opens
 * Charge & Hold Shipment - Actual Ship Items (uOMD0037). The parent only shows this tab for a
 * Charge & Hold shipment. The Change button applies Delphi's gate before opening the dialog; the
 * server re-checks it on open and on save.
 */
function ChargeAndHoldShipmentTab(props) {
    const { formFields, headerStage, onShipItemsSaved, oidc } = props;
    const accessToken = oidc?.user?.access_token;
    const detailRowId = formFields?.Rowid && formFields.Rowid !== EMPTY_GUID ? formFields.Rowid : null;

    const gridRef = useRef(null);
    const [showPopup, setShowPopup] = useState({ visible: false, detailRowId: null });

    const dataSource = useMemo(() => {
        if (!detailRowId || !accessToken) return [];
        return createStore({
            key: "Rowid",
            loadUrl: `${window._env_.REACT_APP_APIS}/${routes.OM.GetChargeAndHoldShipmentLocations}`,
            loadParams: { detailRowId },
            onBeforeSend: (method, ajaxOptions) => {
                ajaxOptions.headers = { Authorization: `Bearer ${accessToken}` };
            },
        });
    }, [detailRowId, accessToken]);

    // uOMD0000 EditChargeAndHoldShipmentLocations. No line, or a history ('Z') line, simply exits; a line with
    // no lot, or a header not yet Assigned, gets an information message (ab_ShowMessage), not an error.
    const handleChange = useCallback(() => {
        if (!detailRowId || formFields?.RecordType === RecordTypes.History) return;
        if (!formFields?.lotId || !stagesAllowingShipItemChanges.includes(headerStage)) {
            alert(MESSAGES.CH_SHIP_STAGE_REQUIRED, MESSAGES.INFORMATION);
            return;
        }
        setShowPopup({ visible: true, detailRowId });
    }, [detailRowId, formFields, headerStage]);

    // Delphi's mrOK branch: RefreshQuery(qryOMTransactionDetail) and LoadChargeAndHoldShipmentLocations(true).
    const handleSaved = useCallback(() => {
        const grid = gridRef.current?.instance();
        if (grid) grid.refresh();
        if (onShipItemsSaved) onShipItemsSaved();
    }, [onShipItemsSaved]);

    return (
        <div style={{ marginTop: 12, height: "100%", display: "flex", flexDirection: "column" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                <strong>Charge &amp; Hold Shipment - Ship Items &amp; Locations</strong>
                <Button
                    text="Change"
                    disabled={!detailRowId}
                    onClick={handleChange}
                />
            </div>
            <div style={{ flex: "1 1 auto", minHeight: 0 }}>
                <DataGrid
                    ref={gridRef}
                    dataSource={dataSource}
                    remoteOperations={true}
                    showBorders={true}
                    columnAutoWidth={true}
                    allowColumnResizing={true}
                    hoverStateEnabled={true}
                    height="100%"
                >
                    <Scrolling mode="standard" />
                    <GridLoadPanel enabled={true} />
                    <Column dataField="Rowid" visible={false} />
                    {/* qryCHShipmentTransLocation ORDER BY: item WH, item, lot, location WH, location. */}
                    <Column dataField="ItemWarehouse" caption="Ship Item WH" />
                    <Column dataField="ItemCode" caption="Ship Item Code" />
                    <Column dataField="LotCode" caption="Ship Lot Code"  />
                    <Column dataField="Warehouse" caption="Location WH"/>
                    <Column dataField="Location" caption="Location Code" />
                    <Column dataField="Originalquantity" caption="Ship Quantity" dataType="number" format={quantityFormat} />
                </DataGrid>
            </div>
            <ChargeAndHoldShipmentPopup showPopup={showPopup} setShowPopup={setShowPopup} onSaved={handleSaved} />
        </div>
    );
}

function mapStateToProps(state) {
    return {
        oidc: state.oidc,
    };
}

export default connect(mapStateToProps)(ChargeAndHoldShipmentTab);
